import * as React from "react";
import styled, { css, useTheme } from "styled-components";

import { ITheme } from "@ui/themes/ThemeInterface";
import { Margins, TestWrapper, Tooltip } from "@ui/Atoms";
import { Info } from "@ui/Assets/Symbolicons/Info";
import { rem } from "@ui/helpers";

import { quarterTime } from "@services/Filters";

enum ICON_STATE {
  DEFAULT = "grey600",
  HOVER = "grey800",
  CHECKED = "blue500",
}

interface ExtraServiceProps {
  children?: React.ReactNode;
  className?: string;
  theme: ITheme;
  icon: React.ElementType;
  isIconWPadding?: boolean;
  label: string;
  time?: number;
  withoutMaxWidth?: boolean;
  value: string;
  isChecked?: boolean;
  onChange?: () => void;
  testId?: string;
  tooltip?: string;
}

const ExtraServiceComponent = (props: ExtraServiceProps) => {
  const theme = useTheme();
  const Icon = props.icon;
  const serviceId = props.label.replace(/ /g, "_");

  const [iconState, setIconState] = React.useState<ICON_STATE>(
    props.isChecked ? ICON_STATE.CHECKED : ICON_STATE.DEFAULT,
  );

  React.useEffect(() => {
    if (props.isChecked) {
      setIconState(ICON_STATE.CHECKED);
    } else {
      setIconState(ICON_STATE.DEFAULT);
    }
  }, [props.isChecked]);

  return (
    <div className={props.className}>
      <StyledCheckbox
        name="cleaning-day"
        id={serviceId}
        type="checkbox"
        onChange={() => {
          props.onChange && props.onChange();
        }}
      />
      <TestWrapper testId={props.testId}>
        <StyledLabel
          htmlFor={serviceId}
          onMouseEnter={() =>
            !props.isChecked && setIconState(ICON_STATE.HOVER)
          }
          onMouseLeave={() =>
            !props.isChecked && setIconState(ICON_STATE.DEFAULT)
          }
        >
          <IconWrapper>
            <Icon
              width={theme.icons.sizes.base_x4}
              color={theme.palette[iconState]}
            />
          </IconWrapper>
          <Margins xs={[null, "half", null, null]}>
            <div>
              <span>{props.label}</span>
              {props.tooltip && (
                <Tooltip
                  position={"bottom"}
                  id={`tooltip-${serviceId}`}
                  placeholder={props.tooltip}
                >
                  <span>
                    <Info
                      variant="solid"
                      color={theme.palette.blue500}
                      size={18}
                      height={18}
                    />
                  </span>
                </Tooltip>
              )}
            </div>
          </Margins>
          {props.time && (
            <StyledCaption data-hj-allow>
              + {quarterTime(props.time)}
            </StyledCaption>
          )}
        </StyledLabel>
      </TestWrapper>
    </div>
  );
};

export const ExtraService = styled(ExtraServiceComponent)<{
  withoutMaxWidth?: boolean;
}>`
  cursor: pointer;
  margin: 0 auto;
  text-align: center;

  ${props =>
    !props.withoutMaxWidth &&
    css`
      max-width: ${rem(100)};
    `}

  svg {
    max-height: ${rem(48)};
  }
`;

const IconWrapper = styled.div`
  min-height: ${rem(40)};
`;

const StyledLabel = styled.label`
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  min-height: ${rem(124)};

  & > div {
    min-height: ${rem(40)};
  }
`;

const StyledCheckbox = styled.input`
  display: none;
`;

const StyledCaption = styled.small`
  font-size: ${props => props.theme.fonts.sizes.caption};
`;
